// CLI v0.8 — `grid ssh [<name>]`.
//
// Spec v17 §13.7: open an interactive shell in a running pod via the API
// (server-side kubectl exec; CLI never spawns kubectl directly). We open a
// WebSocket to `/api/v2/runtimes/:entity_id/exec`, wire stdin/stdout in
// raw-TTY mode, and exit when the server closes the session.
//
// JWT travels as a Bearer header. Browsers can't send Authorization on
// WebSockets, but Node's `ws` package can — and the CLI is the only client
// of this endpoint.

import { Command } from 'commander';
import { Command as CommanderCommand } from 'commander';
import { WebSocket } from 'ws';
import { getV2Client, type IV2Client } from '../api/v2-client.js';
import { handleV2Error } from '../handle-error.js';
import { V2ApiError } from '../api/v2-errors.js';
import { resolveEntityTarget } from '../services/resolve-entity-target.js';
import { readCredentials } from '../credentials.js';
import { loadConfig } from '../config.js';
import { notSignedInMessage } from '../active-org.js';

/**
 * Build the WebSocket URL for the v2 exec endpoint.
 *
 *  - http→ws, https→wss.
 *  - Strips trailing slashes from the base.
 *  - URL-encodes the entity_id and (if provided) the service name.
 */
export function buildExecUrl(apiUrl: string, entityId: string, service?: string): string {
  const base = apiUrl.replace(/\/+$/, '').replace(/^http/i, 'ws');
  const path = `/api/v2/runtimes/${encodeURIComponent(entityId)}/exec`;
  const qs = service ? `?service=${encodeURIComponent(service)}` : '';
  return `${base}${path}${qs}`;
}

export type ExecConnectOptions = { headers: Record<string, string> };
export type ExecConnect = (url: string, opts: ExecConnectOptions) => Promise<void>;

export interface RunSshOpts {
  client: IV2Client;
  name: string | undefined;
  service?: string;
  cwd?: string;
  jwt: string;
  apiUrl: string;
  /** Injected to keep tests off the real WebSocket stack. */
  connect: ExecConnect;
}

export async function runSsh(opts: RunSshOpts): Promise<void> {
  if (!opts.jwt || opts.jwt.trim().length === 0) {
    throw new V2ApiError(401, 'AUTH_REQUIRED', notSignedInMessage());
  }
  const target = await resolveEntityTarget(opts.client, opts.name, opts.cwd ?? process.cwd());
  const url = buildExecUrl(opts.apiUrl, target.entity_id, opts.service);
  await opts.connect(url, {
    headers: { authorization: `Bearer ${opts.jwt}` },
  });
}

/**
 * Real WebSocket session — wires the local TTY to the remote pod's stdin/
 * stdout. Resolves once the remote closes (typically when the user types
 * `exit`). Errors during connect/stream surface as CloudGridError so the
 * top-level handler emits a clean message.
 */
export const realConnect: ExecConnect = (url, { headers }) => {
  return new Promise<void>((resolve, reject) => {
    const ws = new WebSocket(url, { headers });
    let stdinHandler: ((data: Buffer) => void) | null = null;
    const detach = () => {
      if (process.stdin.isTTY) process.stdin.setRawMode(false);
      process.stdin.pause();
      if (stdinHandler) process.stdin.off('data', stdinHandler);
    };

    ws.on('open', () => {
      if (process.stdin.isTTY) process.stdin.setRawMode(true);
      process.stdin.resume();
      stdinHandler = (data: Buffer) => {
        if (ws.readyState === ws.OPEN) ws.send(data);
      };
      process.stdin.on('data', stdinHandler);
    });
    ws.on('message', (data: Buffer) => process.stdout.write(data));
    ws.on('close', () => { detach(); resolve(); });
    ws.on('error', (err) => {
      detach();
      reject(new V2ApiError(500, 'EXEC_FAILED', err.message));
    });
  });
};

export const sshCommand: CommanderCommand = new Command('ssh')
  .description('Open an interactive shell in a running pod (via the API; no local kubectl).')
  .argument('[name]', 'Entity slug or id (defaults to the entity linked in this directory)')
  .option('--service <name>', 'Service name (defaults to the first running pod)')
  .action(async (name: string | undefined, opts: { service?: string }) => {
    try {
      const creds = readCredentials();
      const cfg = loadConfig();
      const client = await getV2Client();
      await runSsh({
        client,
        name,
        service: opts.service,
        jwt: creds?.jwt ?? '',
        apiUrl: cfg.api_url,
        connect: realConnect,
      });
    } catch (err) {
      await handleV2Error(err);
    }
  });
